import {
  LinkProps,
  useLoaderData,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { signOut } from "aws-amplify/auth";
import { Header } from "@/components/header";
import { Main } from "@/components/main";
import { ProfileDropdown } from "@/components/profile-dropdown";
import { Search } from "@/components/search";
import { ThemeSwitch } from "@/components/theme-switch";
import { TopNav } from "@/components/top-nav";

interface Props {
  children: React.ReactNode;
  pageTitle: string;
  pageDescription?: string;
  rightContent?: React.ReactNode;
  topNav?: {
    title: string;
    href: LinkProps["to"];
    isActive: boolean;
    disabled: boolean;
    forceActive?: boolean;
  }[];
}

export default function RootUserLayout({
  children,
  pageTitle,
  rightContent,
  topNav = [],
  pageDescription,
}: Props) {
  const navigate = useNavigate();
  const { email, name } = useLoaderData({ from: "/_root" });
  const location = useLocation();

  // Update the active state based on current route, respecting forceActive
  const updatedTopNav = topNav.map((item) => {
    // Get normalized paths for comparison
    const itemPath = item.href?.toString() ?? "";
    const currentPath = location.pathname;

    // Determine if the item is active
    let isActive = !!item.forceActive;

    if (!isActive) {
      if (itemPath === "/root-user/dashboard") {
        // Exact match for dashboard (prevents matching dashboard/analytics)
        isActive =
          currentPath === "/root-user/dashboard" ||
          currentPath === "/root-user/dashboard/";
      } else if (itemPath.includes("$")) {
        // Handle dynamic routes with parameters (like $id)
        const itemPathParts = itemPath.split("/").filter(Boolean);
        const currentPathParts = currentPath.split("/").filter(Boolean);

        // If parts count doesn't match, it can't be active
        if (itemPathParts.length === currentPathParts.length) {
          isActive = itemPathParts.every((part, index) => {
            // Dynamic parameter (starts with $) or matching static segment
            return part.startsWith("$") || part === currentPathParts[index];
          });
        }
      } else {
        // Normal route - check if current path starts with item path
        // But ensure we match on path boundaries to avoid partial matches
        isActive =
          currentPath === itemPath ||
          (currentPath.startsWith(itemPath) &&
            (itemPath.endsWith("/") ||
              currentPath.charAt(itemPath.length) === "/"));
      }
    }

    return {
      ...item,
      isActive,
    };
  });

  return (
    <>
      <Header>
        <TopNav links={updatedTopNav} />
        <div className="ml-auto flex items-center space-x-4">
          <Search />
          <ThemeSwitch />
          <ProfileDropdown
            name={name ?? "Super Admin"}
            email={email ?? ""}
            onProfileClick={() => {
              navigate({
                to: "/root-user/settings/profile",
              });
            }}
            onSettingsClick={() => {
              navigate({
                to: "/root-user/settings/appearance",
              });
            }}
            onLogout={async () => {
              signOut();
              navigate({
                to: "/root-user/login",
              });
            }}
          />
        </div>
      </Header>

      <Main>
        <div className="mb-2 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">{pageTitle}</h2>
            {pageDescription && (
              <p className="text-muted-foreground">{pageDescription}</p>
            )}
          </div>
          {rightContent}
        </div>
        {children}
      </Main>
    </>
  );
}
